import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Images, RefreshCw } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import MasonryGallery from '../components/MasonryGallery';
import { api } from '../lib/api';
import { toast } from '../hooks/use-toast';

// Local copy of the filter tabs (mock.js is landing-only decoration now).
const VERTICALS = [
  { key: 'all', label: 'All' },
  { key: 'fashion', label: 'Fashion' },
  { key: 'product', label: 'Product' },
  { key: 'beauty', label: 'Beauty' },
  { key: 'editorial', label: 'Editorial' },
];

// The backend returns { id, title, vertical, src, aspect, featured } (see
// GET /gallery in server.py). Field access is tolerant in case an item was
// created with a different image/title key. Clone/view counters are never
// incremented server-side, so they are deliberately not displayed.
const normalize = (raw) => ({
  id: raw.id || raw._id,
  title: raw.title || raw.prompt || 'Untitled',
  vertical: raw.vertical || 'all',
  aspect: raw.aspect || 'square',
  src: raw.src || raw.image || raw.image_url || raw.url || '',
});

export default function Gallery() {
  const [active, setActive] = useState('all');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const load = useCallback(() => {
    setLoading(true);
    setError('');
    api.getGallery()
      .then((list) => setItems((Array.isArray(list) ? list : []).map(normalize).filter((i) => i.src)))
      .catch((e) => setError(e.message || 'Could not load the gallery.'))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const visible = useMemo(
    () => (active === 'all' ? items : items.filter((i) => i.vertical === active)),
    [active, items]
  );

  const handleClone = (item) => {
    toast({ title: 'Sign up to clone', description: `“${item.title}” is ready to remix with your own assets.` });
    setTimeout(() => navigate('/register'), 700);
  };

  return (
    <div className="App min-h-screen bg-background text-foreground">
      <Navbar />
      <main className="mx-auto max-w-7xl px-5 pb-24 pt-28 md:px-8">
        <div className="max-w-2xl">
          <p className="text-sm font-medium uppercase tracking-widest text-node-style">Public gallery</p>
          <h1 className="mt-3 font-serif-display text-4xl leading-tight text-white sm:text-5xl">
            Workflows worth stealing
          </h1>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground">
            Every image here was generated from a shareable node graph. Clone one and swap in your own products, models and styles.
          </p>
        </div>

        <div className="no-scrollbar mt-9 flex gap-2 overflow-x-auto pb-1">
          {VERTICALS.map((v) => (
            <button
              key={v.key}
              onClick={() => setActive(v.key)}
              className={`shrink-0 rounded-full border px-4 py-2 text-sm transition-colors ${
                active === v.key
                  ? 'border-white bg-white text-black'
                  : 'border-border bg-transparent text-muted-foreground hover:border-white/40 hover:text-white'
              }`}
            >
              {v.label}
            </button>
          ))}
        </div>

        <div className="mt-10">
          {loading ? (
            <div className="flex justify-center py-20">
              <Loader2 className="animate-spin text-white/40" />
            </div>
          ) : error ? (
            <div className="rounded-2xl border border-red-500/30 bg-red-500/5 p-10 text-center" data-testid="gallery-error">
              <p className="text-sm text-red-300">Could not load the gallery: {error}</p>
              <button
                onClick={load}
                className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-border px-4 py-2 text-sm text-white/80 transition-colors hover:border-white/40 hover:text-white"
              >
                <RefreshCw size={14} /> Try again
              </button>
            </div>
          ) : visible.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-border p-14 text-center" data-testid="gallery-empty">
              <Images size={28} className="mx-auto text-white/25" />
              <p className="mt-4 text-white/60">
                {active === 'all' ? 'Nothing in the gallery yet.' : 'Nothing here for this vertical yet.'}
              </p>
              <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">
                Curated workflows will appear here as they are published.
              </p>
            </div>
          ) : (
            <MasonryGallery items={visible} onClone={handleClone} />
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
